/**
 * Module WorkspaceIndexedDB - Lớp bọc IndexedDB nhỏ gọn cho Workspace
 * Lưu trữ bền vững (dung lượng lớn, không bị giới hạn 5MB như localStorage):
 * - Store 'recent': danh sách tài liệu mở gần đây (keyPath = id)
 * - Store 'kv'    : cặp khóa/giá trị đơn giản, ví dụ phiên đang hoạt động (keyPath = key)
 *
 * Mọi hàm đều trả về Promise và KHÔNG bao giờ throw ra ngoài:
 * nếu IndexedDB không khả dụng (chế độ ẩn danh cũ, bị chặn...) sẽ resolve giá trị mặc định
 * để phần còn lại của ứng dụng tiếp tục chạy bằng bộ nhớ đệm.
 */

const DB_NAME = 'workspace_db';
const DB_VERSION = 2;

export const IDB_STORES = {
  RECENT: 'recent',
  KV: 'kv',
  VIDEO_RELATED: 'video-related'
};

export class WorkspaceIndexedDB {
  constructor() {
    /** @type {Promise<IDBDatabase|null>|null} Promise mở DB (mở một lần, dùng lại) */
    this._dbPromise = null;
  }

  /**
   * Kiểm tra trình duyệt có hỗ trợ IndexedDB hay không
   * @returns {boolean}
   */
  isSupported() {
    try {
      return typeof indexedDB !== 'undefined' && indexedDB !== null;
    } catch (e) {
      return false;
    }
  }

  /**
   * Mở (hoặc tạo mới) database. Chỉ mở một lần, các lần sau dùng lại kết quả cũ.
   * @returns {Promise<IDBDatabase|null>} null nếu không mở được
   */
  open() {
    if (this._dbPromise) return this._dbPromise;

    this._dbPromise = new Promise((resolve) => {
      if (!this.isSupported()) {
        resolve(null);
        return;
      }

      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        // Tạo cấu trúc store khi DB mới được tạo / nâng version
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(IDB_STORES.RECENT)) {
            const recentStore = db.createObjectStore(IDB_STORES.RECENT, { keyPath: 'id' });
            // Index theo thời gian để sắp xếp "gần nhất" khi đọc
            recentStore.createIndex('lastActiveAt', 'lastActiveAt', { unique: false });
          }
          if (!db.objectStoreNames.contains(IDB_STORES.KV)) {
            db.createObjectStore(IDB_STORES.KV, { keyPath: 'key' });
          }
          if (!db.objectStoreNames.contains(IDB_STORES.VIDEO_RELATED)) {
            db.createObjectStore(IDB_STORES.VIDEO_RELATED, { keyPath: 'videoId' });
          }
        };

        request.onsuccess = () => {
          const db = request.result;
          db.onversionchange = () => {
            db.close();
            this._dbPromise = null;
          };
          resolve(db);
        };
        request.onerror = () => {
          console.warn('[WorkspaceIndexedDB] Không mở được DB:', request.error);
          resolve(null);
        };
        request.onblocked = () => resolve(null);
      } catch (err) {
        console.warn('[WorkspaceIndexedDB] Lỗi khi mở DB:', err);
        resolve(null);
      }
    });

    return this._dbPromise;
  }

  /**
   * Chạy một thao tác trong một transaction và trả về kết quả
   * @param {string} storeName Tên store
   * @param {'readonly'|'readwrite'} mode Chế độ transaction
   * @param {(store: IDBObjectStore) => IDBRequest|void} operation Hàm thao tác trên store
   * @param {any} fallback Giá trị trả về khi lỗi / không có DB
   * @returns {Promise<any>}
   */
  async _run(storeName, mode, operation, fallback) {
    const db = await this.open();
    if (!db) return fallback;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        const request = operation(store);

        tx.oncomplete = () => resolve(request ? request.result : true);
        tx.onerror = () => {
          console.warn(`[WorkspaceIndexedDB] Lỗi transaction (${storeName}):`, tx.error);
          resolve(fallback);
        };
        tx.onabort = () => resolve(fallback);
      } catch (err) {
        console.warn(`[WorkspaceIndexedDB] Lỗi thao tác (${storeName}):`, err);
        resolve(fallback);
      }
    });
  }

  /**
   * Lấy toàn bộ bản ghi của một store
   * @param {string} storeName
   * @returns {Promise<Array<Object>>}
   */
  async getAll(storeName) {
    const result = await this._run(storeName, 'readonly', (s) => s.getAll(), []);
    return Array.isArray(result) ? result : [];
  }

  /**
   * Lấy một bản ghi theo khóa
   * @param {string} storeName
   * @param {string} key
   * @returns {Promise<Object|null>}
   */
  async get(storeName, key) {
    if (!key) return null;
    const result = await this._run(storeName, 'readonly', (s) => s.get(key), null);
    return result || null;
  }

  /**
   * Ghi (thêm mới hoặc cập nhật) một bản ghi
   * @param {string} storeName
   * @param {Object} record Bản ghi phải chứa khóa chính (id hoặc key)
   * @returns {Promise<boolean>}
   */
  async put(storeName, record) {
    if (!record || typeof record !== 'object') return false;
    const result = await this._run(storeName, 'readwrite', (s) => s.put(record), false);
    return result !== false;
  }

  /**
   * Ghi nhiều bản ghi trong cùng một transaction (nhanh và nguyên tử)
   * @param {string} storeName
   * @param {Array<Object>} records
   * @returns {Promise<boolean>}
   */
  async putMany(storeName, records) {
    if (!Array.isArray(records) || records.length === 0) return true;
    const result = await this._run(storeName, 'readwrite', (s) => {
      records.forEach((r) => s.put(r));
    }, false);
    return result !== false;
  }

  /**
   * Xóa một bản ghi theo khóa
   * @param {string} storeName
   * @param {string} key
   * @returns {Promise<boolean>}
   */
  async delete(storeName, key) {
    if (!key) return false;
    const result = await this._run(storeName, 'readwrite', (s) => s.delete(key), false);
    return result !== false;
  }

  /**
   * Xóa nhiều bản ghi theo danh sách khóa trong cùng một transaction
   * @param {string} storeName
   * @param {Array<string>} keys
   * @returns {Promise<boolean>}
   */
  async deleteMany(storeName, keys) {
    if (!Array.isArray(keys) || keys.length === 0) return true;
    const result = await this._run(storeName, 'readwrite', (s) => {
      keys.forEach((k) => s.delete(k));
    }, false);
    return result !== false;
  }

  /**
   * Lấy danh sách tài liệu gần đây được sắp xếp theo thời gian mới nhất (lastActiveAt giảm dần)
   * @param {number} [limit=20] Số lượng mục tối đa cần lấy
   * @returns {Promise<Array<Object>>}
   */
  async getRecentSorted(limit = 20) {
    try {
      const items = await this.getAll(IDB_STORES.RECENT);
      if (!Array.isArray(items)) return [];
      
      // Sắp xếp theo lastActiveAt từ mới nhất đến cũ nhất
      items.sort((a, b) => {
        const timeA = Number(a?.lastActiveAt) || 0;
        const timeB = Number(b?.lastActiveAt) || 0;
        return timeB - timeA;
      });

      const max = Math.max(1, Number(limit) || 20);
      return items.slice(0, max);
    } catch (err) {
      console.warn('[WorkspaceIndexedDB] Lỗi lấy danh sách Recent sắp xếp:', err);
      return [];
    }
  }

  /**
   * Tự động dọn dẹp store 'recent', chỉ giữ lại tối đa `maxCount` tài liệu mới nhất
   * Các tài liệu cũ hơn sẽ bị xóa khỏi IndexedDB
   * @param {number} [maxCount=20]
   * @returns {Promise<boolean>}
   */
  async trimRecent(maxCount = 20) {
    try {
      const items = await this.getAll(IDB_STORES.RECENT);
      if (!Array.isArray(items) || items.length <= maxCount) {
        return true;
      }

      // Sắp xếp theo lastActiveAt giảm dần (mới nhất lên đầu)
      items.sort((a, b) => {
        const timeA = Number(a?.lastActiveAt) || 0;
        const timeB = Number(b?.lastActiveAt) || 0;
        return timeB - timeA;
      });

      // Lấy danh sách ID các mục vượt quá giới hạn để xóa
      const itemsToDelete = items.slice(maxCount);
      const keysToDelete = itemsToDelete.map(i => i.id).filter(Boolean);

      if (keysToDelete.length > 0) {
        console.log(`[WorkspaceIndexedDB] Đang dọn dẹp ${keysToDelete.length} tài liệu cũ khỏi IndexedDB...`);
        return await this.deleteMany(IDB_STORES.RECENT, keysToDelete);
      }

      return true;
    } catch (err) {
      console.warn('[WorkspaceIndexedDB] Lỗi khi cắt giảm danh sách Recent:', err);
      return false;
    }
  }

  /**
   * Đọc cache video liên quan và cập nhật lần truy cập gần nhất.
   * @param {string} videoId
   * @returns {Promise<Array<Object>|null>}
   */
  async getRelatedVideoCache(videoId) {
    if (!videoId) return null;
    const db = await this.open();
    if (!db) return null;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(IDB_STORES.VIDEO_RELATED, 'readwrite');
        const store = tx.objectStore(IDB_STORES.VIDEO_RELATED);
        const request = store.get(videoId);
        let items = null;

        request.onsuccess = () => {
          const record = request.result;
          if (record && Array.isArray(record.items)) {
            items = record.items;
            record.lastAccessedAt = Date.now();
            store.put(record);
          }
        };
        request.onerror = () => {
          console.warn('[WorkspaceIndexedDB] Lỗi đọc cache video liên quan:', request.error);
        };
        tx.oncomplete = () => resolve(items);
        tx.onerror = () => {
          console.warn('[WorkspaceIndexedDB] Lỗi transaction đọc cache video:', tx.error);
          resolve(null);
        };
        tx.onabort = () => resolve(null);
      } catch (err) {
        console.warn('[WorkspaceIndexedDB] Không đọc được cache video:', err);
        resolve(null);
      }
    });
  }

  /**
   * Lưu danh sách liên quan và chỉ giữ cache của 20 video được truy cập gần nhất.
   * @param {string} videoId
   * @param {Array<Object>} items
   * @returns {Promise<boolean>}
   */
  async putRelatedVideoCache(videoId, items) {
    if (!videoId || !Array.isArray(items)) return false;
    const db = await this.open();
    if (!db) return false;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(IDB_STORES.VIDEO_RELATED, 'readwrite');
        const store = tx.objectStore(IDB_STORES.VIDEO_RELATED);
        const now = Date.now();
        store.put({
          videoId,
          items: items.slice(0, 15),
          lastAccessedAt: now,
          updatedAt: now
        });

        const allRequest = store.getAll();
        allRequest.onsuccess = () => {
          const records = allRequest.result || [];
          records.sort((a, b) => (Number(b.lastAccessedAt) || 0) - (Number(a.lastAccessedAt) || 0));
          records.slice(20).forEach(record => store.delete(record.videoId));
        };
        allRequest.onerror = () => {
          console.warn('[WorkspaceIndexedDB] Lỗi dọn cache video liên quan:', allRequest.error);
        };

        tx.oncomplete = () => resolve(true);
        tx.onerror = () => {
          console.warn('[WorkspaceIndexedDB] Lỗi transaction ghi cache video:', tx.error);
          resolve(false);
        };
        tx.onabort = () => resolve(false);
      } catch (err) {
        console.warn('[WorkspaceIndexedDB] Không ghi được cache video:', err);
        resolve(false);
      }
    });
  }
}

// Instance duy nhất dùng chung toàn ứng dụng
export const workspaceDB = new WorkspaceIndexedDB();
